import { Button, FormControl, Grid, Group, IconButton, Layout, Page } from "@/comn/components";
import { useAuth, useFetch, useForm, useGrid, useModal, usePage, useResource, useStore, useToast } from "@/comn/hooks";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnUtils } from "@/comn/utils";
import {
    BASE,
    URLS,
    APIS,
    SF_DOC_TRMSSN_DTL_INFO,
} from "@/tra/tancis/ptli/elctdoc/trmssn/services/PtliDocTrmssnMgmtService";

/*
 * Document transmission management detail information
 * !== 전자문서 발신 관리 상세정보 ==!
 */
export const PTLI0702002S = () => {
    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0047", "auth_ptli0004", "auth_ptli0049", "auth_ptli0015", "auth_ptlianyADM", "auth_ptli0048"],
    });
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast();
    const modal = useModal();
    const { docNtcUuid } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        docTrmssnMgmtInfo: useForm({
            defaultSchema: SF_DOC_TRMSSN_DTL_INFO,
            defaultValues: {
                ...(pgeStore?.form || {}),
            },
        }),
    };

    const fetch = {
        // Gets Document Transmission information. !== 전자문서 발신 정보를 조회한다.==!
        getElctDocTrmssn: useFetch({
            api: () => APIS.getElctDocTrmssn(docNtcUuid),
            enabled: !!docNtcUuid,
            onSuccess: (data) => {
                const elctDocTrmssn = data?.elctDocTrmssn?.content;
                form.docTrmssnMgmtInfo.setValues(elctDocTrmssn);
            },
            onError: (error) => {
                if (error.response.status === 400) {
                    const { errorMessage } = error.response.data;

                    modal.openModal({
                        content: errorMessage.content,
                        onConfirm: () => {
                            navigate(URLS.ptli0702001q);
                        },
                        close: false,
                    });

                    return;
                }

                toast.showToast({ type: "error", content: "msg.com.00018" }); // An error occurred. !== 에러가 발생했습니다! ==!
            },
        }),
        // Update sending electronic documents !== 전자문서 발신 수정 ==!
        updateElctDocTrmssn: useFetch({
            api: (data) => APIS.updateElctDocTrmssn({ ...data }),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.com.00062", // Successfully saved., !== 정상적으로 저장되었습니다. ==!
                    onConfirm: () => {
                        fetch.getElctDocTrmssn.fetch();
                    },
                    close: false,
                });
            },
            onError: (error) => {
                if (error.response.status === 400) {
                    form.docTrmssnMgmtInfo.setErrors(error.response.data.errors);
                    return;
                }

                toast.showToast({ type: "error", content: "msg.com.00018" }); // An error occurred. !== 에러가 발생했습니다! ==!
            },
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // on Save Button Click Event Handler !== 저장 버튼 클릭 ==!
        onResdBtnClick: () => {
            if (!comnUtils.isEmpty(form.docTrmssnMgmtInfo.errors)) {
                toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                return;
            }

            form.docTrmssnMgmtInfo.handleSubmit(
                () => {
                    console.log("modal");
                    modal.openModal({
                        content: "msg.00101", // Do you want to save ? !== 저장 하시겠습니까 ? ==!
                        onConfirm: () => {
                            fetch.updateElctDocTrmssn.fetch(form.docTrmssnMgmtInfo.getValues());
                        },
                    });
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                },
            )();
        },
    };

    /*
     * Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        form.docTrmssnMgmtInfo.setEditable(false);
        fetch.getElctDocTrmssn.fetch();
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control
                                {...form.docTrmssnMgmtInfo.schema.trscLogId}
                                onClick={() => {
                                    const trscLogId = form.docTrmssnMgmtInfo.getValue("trscLogId");

                                    if (comnUtils.isEmpty(trscLogId)) return;

                                    navigate(`${URLS.ptli0702003q}/${trscLogId}`);
                                }}
                            ></Group.Control>
                            <Group.Control {...form.docTrmssnMgmtInfo.schema.dclaDocCd}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.docTrmssnMgmtInfo.schema.docSbmtNo}></Group.Control>
                            <Group.Control {...form.docTrmssnMgmtInfo.schema.intfId}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.docTrmssnMgmtInfo.schema.intfSndrId}></Group.Control>
                            <Group.Control {...form.docTrmssnMgmtInfo.schema.intfRcvrId}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.docTrmssnMgmtInfo.schema.intfChnlId}></Group.Control>
                            <Group.Control {...form.docTrmssnMgmtInfo.schema.elctPrcssStatCdNm}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.docTrmssnMgmtInfo.schema.usrDefnVldVal1}></Group.Control>
                            <Group.Control {...form.docTrmssnMgmtInfo.schema.usrDefnVldVal2}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.docTrmssnMgmtInfo.schema.usrDefnVldVal3}></Group.Control>
                            <Group.Control {...form.docTrmssnMgmtInfo.schema.usrDefnVldVal4}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.docTrmssnMgmtInfo.schema.usrDefnVldVal5}></Group.Control>
                            <Group.Control {...form.docTrmssnMgmtInfo.schema.sysmSavePathNm}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.docTrmssnMgmtInfo.schema.sysmSaveFileNm}></Group.Control>
                            <Group.Control {...form.docTrmssnMgmtInfo.schema.docNtcDtm}></Group.Control>
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>
            <Layout direction="row">
                <Layout.Left>
                    <Button
                        role="list"
                        onClick={() => {
                            navigate(URLS.ptli0702001q);
                        }}
                    ></Button>
                </Layout.Left>
                <Layout.Right>
                    {/* F: In case of failure, create a re-request button !== F:실패인 경우 재요청 버튼 생성 ==! */}
                    <Button
                        role="save"
                        onClick={() => {
                            handler.onResdBtnClick();
                        }}
                    >
                        {t("B_RE_RQST")}
                    </Button>
                </Layout.Right>
            </Layout>
        </Page>
    );
};
